/**
 * /reports/claim-imports/:id — one run: what the file said, what it matched, what changed.
 *
 * Two jobs. Before confirming it is the dry-run diff; after confirming it is the audit
 * record, and it must stay readable as one a year later. That is why every writable row
 * expands to a real before→after on all four fields — a log you cannot check is not a log.
 *
 * ⚠️ THE BROWSER SENDS ROW IDS, NEVER BILLING IDS. The Pages Function looks the billing
 * line up from the staged row. A client that could name a billing id could flip any line
 * in the CRM.
 *
 * ⚠️ WHAT IS PRE-TICKED IS A SAFETY DECISION. `high` rows only. medium/low are shown open
 * and unticked so somebody has to look at them; ambiguous, unmatched and not-payable rows
 * cannot be ticked at all.
 */
import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft, CheckCircle2, AlertTriangle, HelpCircle, XCircle, Clock,
  RotateCcw, Loader2, ChevronDown, ChevronRight, ChevronLeft, RefreshCw,
} from 'lucide-react'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'
import PatientLink from '../../components/PatientLink.jsx'
import { sourceLabel, readRunFilters, runQueryString, filterRuns, compareRuns } from '../../lib/claimRunQuery.js'
import { isAnyChatDockOpen } from '../../lib/chatDock.js'
import { useSearch } from '../../components/SearchProvider.jsx'
import { useMe } from '../../lib/me.js'
import { canSetClaimStatus, canRequestAmountFix, canApproveAmountFix } from '../../lib/role.js'
import { AmountRequestCell, AmountRequestDialog } from '../../components/claim/AmountRequest.jsx'
import LinkControl from '../../components/claim/LinkControl.jsx'
import { runProgress, isRowDone } from '../../lib/claimLink.js'
import { payerTolerance } from '../../data/claimImportPayers.js'
import { apiErrorFromBody } from '../../lib/apiError.js'

const fmt = (n) => Number(n || 0).toLocaleString('th-TH', { maximumFractionDigits: 2 })
const dt = (v) => (v ? new Date(Number(v)).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' }) : '—')
const d10 = (v) => (v ? String(v).slice(0, 10) : '—')

/* Buckets, in the order they must be read: what will change, then what needs a human,
   then what will not change. Money first, noise last. */
const SECTIONS = [
  { key: 'to_write', title: 'พร้อมบันทึก', tone: 'emerald', open: true, writable: true, Icon: CheckCircle2 },
  { key: 'ambiguous', title: 'กำกวม — ต้องตรวจเอง', tone: 'amber', open: true, writable: false, Icon: HelpCircle,
    note: 'จับคู่ได้มากกว่าหนึ่งรายการ หรือมีสองแถวแย่งรายการเดียวกัน ระบบจะไม่เดา' },
  { key: 'unmatched', title: 'จับคู่ไม่ได้', tone: 'rose', open: true, writable: false, Icon: XCircle },
  { key: 'amount_partial', title: 'จ่ายไม่เต็มจำนวน', tone: 'amber', open: true, writable: false, Icon: AlertTriangle,
    /* ⚠️ The old copy said ระบบยังไม่มีสถานะสำหรับกรณีนี้. That stopped being true on
       2026-08-25 when advance_status was widened to pending|received|partial|rejected and
       billing.advance_received_amount was added. Do not re-introduce it. */
    note: 'ประกันจ่ายน้อยกว่าที่เรียกเก็บ — ระบบไม่บันทึกอัตโนมัติ ต้องให้คนตรวจแล้วบันทึกเป็น “รับบางส่วน” พร้อมยอดที่ได้รับจริง' },
  { key: 'amount_mismatch', title: 'จำนวนเงินไม่ตรง', tone: 'rose', open: false, writable: false, Icon: AlertTriangle },
  { key: 'not_payable', title: 'ยังไม่จ่าย', tone: 'slate', open: false, writable: false, Icon: Clock },
  /* ⚠️ THIS BUCKET NOW HOLDS THE OLD `date_differs` ROWS TOO. Owner, 2026-09-16: the payer's
     transfer date is recorded, never matched on, so a date disagreement is no longer its own
     group — it was 40 rows of work to correct a bookkeeping field.
     ⚠️ `open` and `writable` are now TRUE. These rows still need LINKING even though they need
     no money: the bill is settled and only the record of which file paid it is missing. The
     gate returns `already_received`, which writes nothing at all, so a whole group can be
     ticked in one press for ฿0. Leaving it collapsed and read-only is what kept 117 rows of
     pure bookkeeping invisible. */
  { key: 'already_received', title: 'รับแล้วอยู่ก่อนหน้า — เหลือแค่ผูกกับไฟล์', tone: 'slate', open: true, writable: true, Icon: CheckCircle2,
    note: 'บิลเหล่านี้บันทึกรับเงินไว้แล้ว — การผูกตรงนี้ไม่แก้ยอดและไม่แก้วันที่เดิม เป็นการเติมประวัติว่าเงินก้อนนี้มาจากไฟล์ไหน' },
  /* ⚠️ MONEY DONE, RECORD MISSING — and it is real work, not a cosmetic state. These rows
     were written by the OLD batch path before linking existed: `write_confirmed` is true and
     `link_state` is empty, so billing is settled but claim_import_link_event holds NOTHING
     for them. api 654's own doctrine is that the events ARE the record and link_state is
     rebuilt from them — so 122 rows with no events is 122 rows that a rebuild would silently
     un-link. Measured 2026-09-15: linking them today writes NOTHING on 117 of the 122
     (already_received) and writes `received` on 5 whose billing has since drifted back to
     unpaid. Draining this section is how the audit trail gets completed by a real person
     against a live read, instead of by a migration fabricating events after the fact.
     ⚠️ It is DELIBERATELY not counted as outstanding money — the ผูกแล้ว tile answers "is
     there money left to handle", and for these there is not. This section answers a
     different question: is the paperwork behind it complete. */
  { key: 'needs_link', title: 'บันทึกเงินแล้ว — แต่ยังไม่ได้ผูกกับไฟล์', tone: 'amber', open: true, writable: true, Icon: Clock,
    note: 'รอบนี้บันทึกเงินไว้ตั้งแต่ก่อนมีระบบผูก เงินเข้าระบบเรียบร้อยแล้ว — การผูกตรงนี้คือการเติมประวัติให้ครบว่าใครผูกกับไฟล์ไหน เกือบทั้งหมดจะไม่แก้ยอดหรือวันที่ใด ๆ' },

  /* ⚠️ ผูกแล้ว, NOT บันทึกแล้ว. This bucket now holds every linked row, and one of the four
     link kinds — already_received — writes NOTHING to billing: the money was already in and
     only the link was missing. Titling the section "บันทึกแล้ว" would claim this run recorded
     money it never touched, which is the same class of untruth as the green บันทึกแล้ว that
     sat on 47 unfinished runs. The per-row chip says WHICH of the four kinds it was. */
  { key: 'written', title: 'ผูกแล้ว', tone: 'emerald', open: true, writable: false, Icon: CheckCircle2,
    note: 'ผูกกับไฟล์ของผู้จ่ายแล้ว — ดูที่ชิปแต่ละแถวว่าเป็นการบันทึกรับเงิน แก้วันที่ หรือรับไว้ก่อนแล้ว (ไม่แก้ยอด)' },
  { key: 'reverted', title: 'ย้อนกลับแล้ว', tone: 'rose', open: true, writable: false, Icon: RotateCcw,
    note: 'รอบนี้เคยบันทึกเงินเข้าไปแล้ว และถูกย้อนกลับ — เงินถูกคืนเป็นค้างรับ ไม่ใช่รายการที่ไม่ได้ทำอะไร' },
  { key: 'skipped', title: 'ข้าม', tone: 'slate', open: false, writable: false, Icon: Clock },
  { key: 'error', title: 'ผิดพลาด', tone: 'rose', open: true, writable: false, Icon: XCircle },
]

/* The four acts that share the word ผูก. Mirrors LinkControl's KIND_LABEL — four different
   things done to money, which must not collapse into one word on screen. */
const LINK_KIND_TH = {
  received: 'บันทึกรับเงิน',
  date_fix: 'แก้วันที่เงินเข้า',
  already_received: 'รับไว้ก่อนแล้ว (ไม่แก้ยอด)',
  partial: 'รับบางส่วน',
  manual: 'ระบุบิลเอง',
}

const TONE = {
  emerald: 'border-emerald-200 bg-emerald-50/60',
  amber: 'border-amber-200 bg-amber-50/60',
  rose: 'border-rose-200 bg-rose-50/60',
  slate: 'border-slate-200 bg-slate-50/60',
}
const CONF = {
  high: { label: 'สูง', cls: 'bg-emerald-100 text-emerald-800' },
  medium: { label: 'กลาง', cls: 'bg-amber-100 text-amber-800' },
  low: { label: 'ต่ำ', cls: 'bg-rose-100 text-rose-800' },
  none: { label: '—', cls: 'bg-slate-100 text-slate-500' },
}

export default function ClaimImportRun() {
  const { id } = useParams()
  const me = useMe()
  // GM/Admin — the same set as /api/billing/status, because ยืนยัน writes that field.
  // Governs REVERT too. The manager-asks / GM-approves split below is deliberate and stays.
  const mayCommit = canSetClaimStatus(me)
  const mayAskAmount = canRequestAmountFix(me)
  const mayOkAmount = canApproveAmountFix(me)

  const [run, setRun] = useState(null)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [picked, setPicked] = useState(() => new Set())
  const [open, setOpen] = useState(() => new Set(SECTIONS.filter((s) => s.open).map((s) => s.key)))
  const [expanded, setExpanded] = useState(() => new Set())
  const [confirm, setConfirm] = useState(null)
  /* Empty on a courier-staged AXA run — its file states no payment date, and the robot is
     forbidden from inventing one. Without this input the run can never be committed. */
  const [txDate, setTxDate] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState(null)
  /* ขอแก้ยอดเบิก — one request per staged row, keyed by row id. Fetched alongside the run
     because the approver's only route to it is this page (owner, 2026-08-25: no notify). */
  const [live, setLive] = useState(() => new Map())
  const [amountReqs, setAmountReqs] = useState([])
  const [askRow, setAskRow] = useState(null)

  /* ── prev/next ─────────────────────────────────────────────────────────────
     The neighbour set is the SAME filtered list the history table shows, resolved from the
     query string the list handed us. Refetched rather than passed through router state:
     state dies on a reload, in a new tab, and on the deep links that already exist. */
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { open: searchOpen } = useSearch()
  const [neighbours, setNeighbours] = useState({ list: [], index: -1, pinned: false })

  /* ⚠️ REQUEST-ID GUARD. Two sequential fetches with no ordering guarantee: arrow-key
     navigation changes `id` faster than Xano answers, so without this, responses land out
     of order and paint run #625's rows under run #623's URL — on the one screen whose
     stated job is to be readable as a money record a year later. Same `alive` idea as
     ClaimImports.jsx, expressed as a counter because `id` can change mid-flight. */
  const reqRef = useRef(0)
  const load = useCallback(async () => {
    const my = ++reqRef.current
    setLoading(true); setErr('')
    try {
      const r = await fetch(`/api/claim-import/run/${id}`, { credentials: 'same-origin' })
      const j = await r.json().catch(() => ({}))
      if (reqRef.current !== my) return
      if (!r.ok) { setErr(apiErrorFromBody(j, r.status, 'โหลดรอบนำเข้าไม่สำเร็จ')); return }
      setRun(j.run || null)
      // Live billing, joined by the proxy. Empty if that read failed — the page still works.
      setLive(new Map((Array.isArray(j.billing) ? j.billing : []).map((b) => [Number(b.id), b])))
      const list = Array.isArray(j.rows) ? j.rows : []
      setRows(list)
      // Non-fatal: a run still reads correctly if this 404s before the endpoint ships.
      try {
        const q = await fetch(`/api/claim-import/amount-request?run_id=${id}`, { credentials: 'same-origin' })
        const qj = await q.json().catch(() => ({}))
        if (reqRef.current !== my) return
        setAmountReqs(q.ok && Array.isArray(qj.requests) ? qj.requests : [])
      } catch { if (reqRef.current === my) setAmountReqs([]) }
    } catch (e) {
      if (reqRef.current === my) setErr(String(e))
    } finally {
      if (reqRef.current === my) setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  /* ⚠️ THE HIGHEST-RISK LINE IN THE PREV/NEXT FEATURE. `txDate` is component state, is NOT
     reset by load(), and goes straight into the commit body. Until prev/next existed this
     route was only ever entered from another route, so the component unmounted and the
     state died with it. Changing `:id` in place does NOT unmount — so a bank-statement date
     typed for an AXA run would still be in the box, and in the POST, for the NEXT run.
     That stamps one run's transfer date onto another run's money, which is precisely the
     defect the transfer-date input was added to prevent. `msg` goes too: a success banner
     about a DIFFERENT run's money is worse than no banner. */
  useEffect(() => {
    setTxDate('')
    setMsg(null)
    setExpanded(new Set())
    // Layout.jsx's <main> is the real scroller — window.scrollTo is a no-op here.
    document.querySelector('main')?.scrollTo({ top: 0 })
  }, [id])

  useEffect(() => {
    let alive = true
    const f = readRunFilters(searchParams)
    const q = new URLSearchParams({ per_page: '500' })
    if (f.payer) q.set('payer', f.payer)
    // ⚠️ NOT cachedGet — it memoises for the session, and a staged→committed run goes stale.
    fetch(`/api/claim-import/runs?${q}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive) return
        const list = filterRuns(Array.isArray(j?.runs) ? j.runs : [], f)
        let i = list.findIndex((x) => Number(x.id) === Number(id))
        let pinned = false
        /* ⚠️ THE COMMIT CASE, and it is not optional. The natural working filter is
           สถานะ=รอยืนยัน. Open a run, tick, commit, and it becomes `committed` — filtered
           out of its own list. findIndex → -1 → the nav goes dead at exactly the moment
           the operator wants →. Splice it back in by created_at and say so. */
        if (i < 0 && run) {
          // Splice by the list's OWN order — it may be sorted by วันที่ส่ง, not by import.
          const cmp = compareRuns(f.sort)
          i = list.findIndex((x) => cmp(run, x) < 0)
          if (i < 0) i = list.length
          list.splice(i, 0, run)
          pinned = true
        }
        setNeighbours({ list, index: i, pinned })
      })
      .catch(() => { if (alive) setNeighbours({ list: [], index: -1, pinned: false }) })
    return () => { alive = false }
  }, [id, searchParams, run])

  /* row id → its request. One per row; Xano keeps only the live one (`active`). */
  const reqByRow = useMemo(() => {
    const m = new Map()
    for (const q of amountReqs) m.set(Number(q.claim_import_row_id), q)
    return m
  }, [amountReqs])
  const pendingReqs = useMemo(() => amountReqs.filter((q) => q.status === 'pending'), [amountReqs])

  /* ⚠️ A COLLAPSED SECTION IS A QUEUE NOBODY SEES, which is the same failure the owner
     avoided by refusing a notification: the approver has to be able to find this by
     opening the run. `amount_mismatch` ships `open: false`, so force both money sections
     open whenever the run is carrying a decision for somebody. */
  useEffect(() => {
    if (!pendingReqs.length) return
    setOpen((p) => new Set([...p, 'amount_mismatch', 'amount_partial']))
  }, [pendingReqs.length])

  const grouped = useMemo(() => {
    const m = new Map()
    for (const r of rows) {
      /* ⚠️ A REVERTED ROW IS NOT A SKIPPED ROW. commit.js must keep writing `skipped` on a
         revert because `outcome` is a Xano enum with no `reverted` token, so the separation
         happens HERE. `reverted_by_run_id` is a safe discriminator: written in exactly one
         place, only when the rollback was confirmed by a read-back, and a reverted run can
         never be re-committed — so it is write-once. It also retro-fixes historic rows.
         Unknown/empty tokens fall to `skipped` so no row can ever render in no section. */
      /* ⚠️ A ROW THIS RUN REVERTED, THAT SOMEBODY HAS SINCE SET BACK TO รับแล้ว, IS NOT
         "ย้อนกลับแล้ว" ANY MORE. That bucket described the run's history; the operator reads
         it as a statement about the money. Measured case: billing #1337194 was reverted to
         pending by this run and set to received by a person on 14 ก.ย. 13:15 — and still sat
         under ย้อนกลับแล้ว, because nothing on the page had ever read the live value.
         Live state wins wherever we have it; the history stays visible as a chip. */
      const b = live.get(Number(r.billing_id))
      const liveReceived = b && String(b.advance_status || '').toLowerCase() === 'received'
      /* ⚠️ A LINKED ROW IS DONE, WHATEVER `outcome` STILL SAYS. Sections used to key on
         `outcome` alone — but `outcome` is the MATCHER's verdict, frozen at import, and the
         bulk link deliberately does not touch it (link_state is the only mutable axis now).
         The old batch path DID rewrite it, which is why rows committed that way moved and
         rows linked the new way did not: on run #61 a row wearing a green
         ผูกแล้ว · บันทึกรับเงิน chip sat under the heading พร้อมบันทึก — "matched, not yet
         written" — about a row the operator had written himself minutes earlier. Owner
         caught it, 2026-09-15. link_state wins over outcome here, exactly as live billing
         wins over the run's history above. */
      /* ⚠️ 40 ROWS IN THE DATABASE STILL CARRY outcome `date_differs` AND ALWAYS WILL —
         `outcome` is frozen at stage time and nothing rewrites it. Nothing produces the token
         any more, so it is folded in with already_received here rather than left to render in
         a section that no longer exists, which would have dropped 40 rows off the screen
         entirely. */
      const k = String(r.link_state || '') === 'linked'
        ? 'written'
        : (r.write_confirmed === true && !Number(r.reverted_by_run_id))
          ? 'needs_link'
        : Number(r.reverted_by_run_id) > 0
          ? (liveReceived ? 'written' : 'reverted')
          : (r.outcome === 'date_differs' ? 'already_received' : (r.outcome || 'skipped'))
      if (!m.has(k)) m.set(k, [])
      m.get(k).push(r)
    }
    return m
  }, [rows, live])

  /* ⚠️ `isStaged` IS GONE FROM THIS PREDICATE, and that single word is what stranded the
     money. commit.js refused any run that was not `staged`, so the moment somebody confirmed
     a subset the rest could never be bulk-actioned again — 166 rows / ฿271,525 sat exactly
     there. The batch route reads no run status at all; it gates per ROW, on a live read. */
  /* One file row can pay SEVERAL billing lines (a visit's filling + scaling in one insurer
     row; a GoWabi voucher billed in halves). They are separate rows sharing the file row's
     `seq`, and the server links them all-or-nothing (link-batch.js, split_incomplete) — so
     a tick here ticks the whole set, rather than letting the operator build a selection
     the server will refuse. */
  const splitSets = useMemo(() => {
    const bySeq = new Map()
    for (const r of rows) {
      if ((r.kind && r.kind !== 'file_row') || !Number(r.seq) || !r.billing_id) continue
      if (!bySeq.has(r.seq)) bySeq.set(r.seq, [])
      bySeq.get(r.seq).push(r.id)
    }
    const m = new Map()
    for (const ids of bySeq.values()) if (ids.length > 1) ids.forEach((id) => m.set(id, ids))
    return m
  }, [rows])

  const selectable = useMemo(
    () => new Set(rows.filter((r) => r.billing_id && String(r.link_state || '') !== 'linked'
      && (r.outcome === 'to_write' || r.outcome === 'date_differs'
        // ⚠️ already_received is selectable, and that is the point: the bill is settled and
        // only the link is missing, so the gate writes NOTHING and a group clears for ฿0.
        || r.outcome === 'already_received'
        // money already written by the old path, link record still missing — see SECTIONS
        || (r.write_confirmed === true && !Number(r.reverted_by_run_id)))).map((r) => r.id)),
    [rows])

  const chosen = useMemo(() => rows.filter((r) => picked.has(r.id) && selectable.has(r.id)), [rows, picked, selectable])
  const chosenAmount = chosen.reduce((t, r) => t + (Number(r.file_amount_paid) || 0), 0)

  const revertable = useMemo(
    () => rows.filter((r) => r.write_confirmed === true && !Number(r.reverted_by_run_id)), [rows])

  // True only when NOTHING can supply a date: not the run, not the rows themselves.
  const needsDate = !run?.transfer_date && !rows.some((r) => r.file_transfer_date)
  const isCommitted = run?.status === 'committed' || run?.status === 'partially_committed'

  /* ── ผูกแล้ว / Done — the Level A counter ──────────────────────────────────
     ⚠️ THE DENOMINATOR EXCLUDES ROWS THAT COULD NEVER BE LINKED — not_payable (the insurer
     itself says it has not paid) and rows dismissed with a reason. If they stayed in, the
     counter could never close and people would learn to ignore it.
     ⚠️ AND IT CARRIES BAHT, NOT ONLY A COUNT. Prudential marks a row not_payable when one
     cell (Coversheet) is blank — an emptiness test, not a statement about money — while
     that row can still carry a real Payable Amt. Three such rows on a 29-row file is ฿8,940
     that Prudential actually transferred, sitting outside the denominator while the tile
     reads 26/26 and looks finished. "26/26" alone is a lie; "26/26 · ยังไม่ผูก ฿8,940" is
     the truth. The identity that must hold: file total = linked + dismissed + unlinked. */
  /* ⚠️ FIXED 2026-09-15 — THIS TILE USED TO COUNT ONLY `link_state === 'linked'`, AND SO IT
     OVERSTATED THE BACKLOG ON EVERY RUN COMMITTED THROUGH THE OLD BATCH PATH. All 124 rows
     that api 628 wrote and read back carry `write_confirmed` and NO link_state, so run #88
     — which really did pay ฿2,750.21 — rendered `ผูกแล้ว 0 / 8 · ยังไม่ผูก ฿12,358`, money it
     had already accounted for. The rule now lives in one file and both screens share it;
     `LinkControl` already reached the same conclusion its own way (`undoOnly`), which is why
     บันทึกแล้ว rows offer ยกเลิก rather than ผูก. See src/lib/claimLink.js. */
  const doneStats = useMemo(() => {
    const p = runProgress(rows)
    return {
      linked: p.done,
      total: p.total,
      openBaht: p.openBaht,
      outsideCount: p.dismissed + p.outside,
      outsideBaht: p.outsideBaht,
    }
  }, [rows])

  /* ── prev/next mechanics ─────────────────────────────────────────────────── */
  const { list: navList, index: navIndex, pinned: navPinned } = neighbours
  const prevRun = navIndex > 0 ? navList[navIndex - 1] : null
  const nextRun = navIndex >= 0 && navIndex < navList.length - 1 ? navList[navIndex + 1] : null

  /* ⚠️ THE PRE-TICK NO LONGER ASKS ABOUT CONFIDENCE, and that is the second half of why
     ฿403,736 sat unwritten. The old rule ticked `high` only — but AIA prints a per-claim
     reference on 1 row in 237 and Generali's file shares no reference with the CRM at all,
     so those two payers are STRUCTURALLY `medium` and were never ticked. 347 of 466 rows are
     medium. Staff ticked the green section and confirmed, exactly as the screen told them.

     Confidence is now a chip and a sort key. What makes a write SAFE is not the matcher's
     self-assessment, it is the live amount check the server runs at click time — so the tick
     proposes every row the server can act on, and the server refuses the rest BY NAME. */
  /* ⚠️ ONE RULE, IN ONE PLACE, AND IT HAD TO BECOME AN EFFECT. The tick used to be set inside
     load() with its own copy of the predicate — so when the confidence rule was removed from
     `selectable`, load() went on ticking `confidence === 'high'` and the two silently
     disagreed. Every page then opened with picked !== selectable, which the old dirty-check
     read as "the operator has been editing this" before anyone had touched it: dead arrow
     keys and a warning banner on a freshly loaded page. Keyed on `rows`, which changes only
     on a load or a hop, so it also clears a stale selection when prev/next swaps the run. */
  /* ⚠️ A RUN OPENS WITH NOTHING TICKED, AND THAT IS A SAFETY RULE, NOT A PREFERENCE.
     For about an hour this page pre-ticked every actionable row so one press could clear a
     whole run. On a 187-row AIA file that arms a money button before the operator has read a
     single line — open it, press ตรวจสอบและยืนยัน out of habit, and 187 rows are written
     unreviewed. That is the same shape as the failure this whole project exists to undo:
     people pressing the green button because it was green. Owner, 2026-09-15.
     Arming is now one deliberate click — เลือกทั้งหมด in the bar, or a section's own
     เลือก/ยกเลิกทั้งกลุ่ม — which costs one press and cannot happen by accident.
     Keyed on `rows`, so it also clears when prev/next swaps the run under a mounted page. */
  useEffect(() => { setPicked(new Set()) }, [rows])

  /* ⚠️ LEAVING A RUN JUST CLEARS THE TICKS — it no longer asks. The confirmation made sense
     when the tick was high-confidence-only, because every extra tick WAS deliberate human
     work worth protecting. Now the page proposes every actionable row, so un-ticking one is
     the common case and the dialog fired on every hop for nothing. A ticked box commits
     nothing; the only thing lost by leaving is a selection the next page rebuilds anyway. */
  const go = useCallback((delta) => {
    const target = delta < 0 ? prevRun : nextRun
    if (!target || busy) return
    setPicked(new Set())
    const qs = searchParams.toString()
    navigate(`/reports/claim-imports/${target.id}${qs ? `?${qs}` : ''}`)
  }, [prevRun, nextRun, busy, searchParams, navigate])

  /* ⚠️ A window listener, not a DOM sniff. No other global arrow handler exists in the app,
     and the route's own mount/unmount is the right lifetime. Every bail below is load-bearing:
       · e.repeat — holding → fires ~20 keydowns while the async resolver takes ~300ms, so
         hops 2-6 all navigate to the page you are already on. Reads as a broken key.
       · modifiers — on macOS ⌥← and ⌘← are history-back gestures; without this, "go back"
         would both pop history AND hop a run.
       · confirm/askRow as STATE, not a [role=dialog] query — AmountRequestDialog has no
         portal, no focus trap and no role, and is built on a radio group where ←/→ have
         native meaning. Safari does not focus a clicked radio, so e.target stays <body>
         and the tag check below would pass.
       · isAnyChatDockOpen — ChatDockHost sits above every route; focus on a message bubble
         is not an input, so ← would drive the page underneath it. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      if (e.repeat) return
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey || e.isComposing) return
      const t = e.target
      const tag = (t?.tagName || '').toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return
      if (confirm || askRow || searchOpen || isAnyChatDockOpen()) return
      if (busy) return
      e.preventDefault()
      go(e.key === 'ArrowLeft' ? -1 : 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [go, confirm, askRow, searchOpen, busy])

  const gates = run?.gates_json || {}
  const gateList = ['g1', 'g2', 'g3', 'g4'].map((k) => ({ k, ...(gates[k] || {}) })).filter((g) => g.detail != null)

  /* ตรวจจับคู่ใหม่ — ask the matcher to look again, against the CRM as it is today.
     ⚠️ Rewrites the MATCH only: no billing write, no link, no event. Rows that are already
     linked or dismissed are left alone by the server. */
  async function rematch(rowId) {
    setBusy(true); setMsg(null)
    try {
      const r = await fetch(`/api/claim-import/run/${id}/rematch`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify({ row_id: rowId }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) { setMsg({ bad: true, text: apiErrorFromBody(j, r.status, 'จับคู่ใหม่ไม่สำเร็จ') }); return }
      const bits = []
      if (j.changed) bits.push(`อัปเดต ${fmt(j.changed)} แถว`)
      if (j.added) bits.push(`พบการแบ่งบิลเพิ่ม ${fmt(j.added)} รายการ`)
      if ((j.shrank || []).length) bits.push('⚠️ แถวนี้เดิมแบ่งเป็นหลายรายการ ต้องตรวจเอง')
      setMsg({ bad: false, text: bits.length ? bits.join(' · ') : (j.detail || 'จับคู่ใหม่แล้ว — ผลเหมือนเดิม') })
      load()
    } catch (e) {
      setMsg({ bad: true, text: String(e) })
    } finally {
      setBusy(false)
    }
  }

  async function submit(revert) {
    setBusy(true); setMsg(null); setConfirm(null)
    try {
      /* ⚠️ TWO DIFFERENT ROUTES. Linking goes to /link-batch, which costs a FIXED 4 + G
         subrequests for any number of rows and shares its gate with the per-row button.
         Reverting a whole run is a separate act and still goes to /commit, untouched. */
      const ids = revert ? revertable.map((r) => r.id) : chosen.map((r) => r.id)
      const path = revert ? 'commit' : 'link-batch'
      const r = await fetch(`/api/claim-import/run/${id}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: revert
          ? JSON.stringify({ row_ids: ids, revert: true, transfer_date: '' })
          : JSON.stringify({ row_ids: ids, transfer_date: txDate }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) { setMsg({ bad: true, text: apiErrorFromBody(j, r.status, 'ทำรายการไม่สำเร็จ') }); return }
      /* ⚠️ THE BREAKDOWN BY KIND IS NOT DECORATION. `already_received` wrote NOTHING to
         billing — the money was already in and only the link was missing, which is the
         majority case here (117 of 263 measured rows). Reporting "บันทึก 146 รายการ" over a
         batch where most rows moved no money would be a lie in the operator's favour. */
      setMsg({
        bad: !j.ok,
        text: revert ? `ย้อนกลับ ${fmt(j.written)} รายการ` : (() => {
          const k = j.by_kind || {}
          const parts = []
          if (k.received) parts.push(`บันทึกรับแล้ว ${fmt(k.received)}`)
          if (k.date_fix) parts.push(`แก้วันที่ ${fmt(k.date_fix)}`)
          if (k.already_received) parts.push(`รับไว้ก่อนแล้ว ${fmt(k.already_received)} (ไม่แก้ยอด)`)
          const sk = j.skipped || []
          const amt = sk.filter((x) => x.why === 'amount_mismatch' || x.why === 'file_exceeds_crm')
          const split = sk.filter((x) => x.why === 'split_incomplete')
          const other = sk.length - amt.length - split.length
          return `ผูกแล้ว ${fmt(j.linked)} รายการ · ${fmt(j.amount_linked)} ฿`
            + (parts.length ? ` — ${parts.join(' · ')}` : '')
            + (amt.length ? ` · ${fmt(amt.length)} รายการยอดไม่ตรง ต้องกดทีละรายการเพื่อเลือกรับบางส่วน` : '')
            + (split.length ? ` · ${fmt(split.length)} รายการไม่ได้ผูก เพราะอยู่ในชุดเดียวกับรายการที่ผูกไม่ได้ (ต้องผูกพร้อมกันทั้งชุด)` : '')
            + (other ? ` · ข้าม ${fmt(other)} รายการ` : '')
            + ((j.unconfirmed || []).length ? ` · ⚠️ ${fmt(j.unconfirmed.length)} รายการยืนยันผลไม่ได้ — เปิดหน้านี้ใหม่` : '')
        })(),
      })
      load()
    } catch (e) {
      setMsg({ bad: true, text: String(e) })
    } finally {
      setBusy(false)
    }
  }

  /* ⚠️ `&& !run` — on a prev/next hop the run is already on screen and re-blanking the whole
     page would take the nav buttons with it, leaving them unclickable for half of every hop.
     Only a genuine cold load (no run yet) gets the full-page state; a hop keeps the page and
     shows a spinner beside the heading instead. Every hook sits above this line. */
  if (loading && !run) return <div className="px-10 py-8 text-[14px] text-slate-500">กำลังโหลด…</div>
  if (err) return <div className="px-10 py-8"><div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">{err}</div></div>
  if (!run) return null

  return (
    <div className="px-10 py-8 max-w-[1300px]">
      {/* ⚠️ STICKY. A Prudential run is one file per branch and hundreds of rows; by the time
          the operator is reading กำกวม at row 140 this header is 2000px above, and with the
          keyboard deliberately disabled on dirty ticks the feature would be unreachable by
          mouse AND keyboard mid-review. The only other sticky element on this page renders
          solely under selectable.size. */}
      <div className="sticky top-0 z-10 -mx-10 px-10 pt-1 pb-3 bg-white/95 backdrop-blur">
        <Link to={`/reports/reimbursement?tab=insurer${searchParams.toString() ? `&${searchParams}` : ''}`}
          className="inline-flex items-center gap-1 text-[13px] text-slate-500 hover:text-brand-600 mb-2">
          <ArrowLeft size={14} /> ประวัติการนำเข้า
        </Link>

        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight">รอบนำเข้า #{run.id}</h1>
          <span className="text-[15px] text-slate-600">{run.payer}</span>
          {loading ? <Loader2 size={15} className="animate-spin text-slate-400" /> : null}

          <div className="ml-auto flex items-center gap-1.5">
            <button type="button" onClick={() => go(-1)} disabled={!prevRun || busy}
              aria-label="รอบก่อนหน้าในรายการ"
              title={prevRun ? `รอบ #${prevRun.id} · ${prevRun.payer} · ${dt(prevRun.created_at)}` : 'ไม่มีรอบก่อนหน้าในรายการนี้'}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-35 disabled:cursor-not-allowed">
              <ChevronLeft size={16} />
            </button>
            {/* Copy says POSITION, never time: api 629's sort order is undocumented, so
                ก่อนหน้า/ถัดไป reading as older/newer could simply be wrong. */}
            <span className="text-[12.5px] text-slate-500 tabular-nums min-w-[86px] text-center">
              {navIndex >= 0 && navList.length ? `รอบที่ ${navIndex + 1} จาก ${navList.length}` : '—'}
            </span>
            <button type="button" onClick={() => go(1)} disabled={!nextRun || busy}
              aria-label="รอบถัดไปในรายการ"
              title={nextRun ? `รอบ #${nextRun.id} · ${nextRun.payer} · ${dt(nextRun.created_at)}` : 'ไม่มีรอบถัดไปในรายการนี้'}
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-35 disabled:cursor-not-allowed">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        <p className="text-[13px] text-slate-500 mt-0.5">
          {sourceLabel(run.source)} · {dt(run.created_at)}
        </p>

        {navPinned ? (
          <div className="mt-2 rounded-lg border border-violet-200 bg-violet-50/70 px-3 py-1.5 text-[12px] text-violet-900">
            รอบนี้ไม่อยู่ในตัวกรองแล้ว — ยังเดินหน้า/ถอยหลังในรายการเดิมได้
          </div>
        ) : null}
      </div>

      <div className="mb-6" />

      {/* ที่มา — provenance. What file, read by which adapter, and did it reconcile. */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm mb-6">
        <div className="text-[13px] font-semibold text-slate-800 mb-3">ที่มา</div>
        <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-3 text-[13px]">
          <Row k="ไฟล์" v={run.file_name || '—'} />
          <Row k="ขนาด" v={run.file_bytes ? `${fmt(run.file_bytes)} bytes` : '—'} />
          <Row k="SHA-256" v={<code className="text-[11.5px] break-all">{(run.file_sha256 || '').slice(0, 24)}…</code>} />
          <Row k="ตัวอ่านไฟล์" v={run.adapter_version || '—'} />
          <Row k="ชีต" v={run.sheet_name || '—'} />
          <Row k="สาขาในไฟล์" v={run.branch_label_raw || '—'} />
          <Row k="ช่วงวันที่รักษา" v={`${d10(run.period_start)} → ${d10(run.period_end)}`} />
          <Row k="เลขที่ใบสำคัญ" v={run.statement_no || '—'} />
          <Row
            k="วันที่เงินเข้าบัญชี"
            v={<>
              {d10(run.transfer_date)}
              <span className="text-slate-400 ml-1">
                ({run.transfer_date_source === 'operator_entered' ? 'กรอกเอง'
                  : run.transfer_date_source === 'absent' ? 'ไม่มีในไฟล์' : 'จากไฟล์'})
              </span>
            </>}
          />
          <Row k="ยอดรวมในไฟล์" v={`${fmt(run.declared_total)} ฿`} />
          {run.declared_wht ? <Row k="หัก ณ ที่จ่าย" v={`${fmt(run.declared_wht)} ฿`} /> : null}
          {run.declared_net ? <Row k="ยอดสุทธิเข้าบัญชี" v={<b>{fmt(run.declared_net)} ฿</b>} /> : null}
          {run.email_from ? <Row k="ผู้ส่ง" v={run.email_from} /> : null}
          {run.confirmed_by_label ? <Row k="ยืนยันโดย" v={`${run.confirmed_by_label} · ${dt(run.confirmed_at)}`} /> : null}
        </dl>

        {gateList.length ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {gateList.map((g) => (
              <span key={g.k}
                className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[12px] ${
                  g.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-800'}`}
                title={g.detail}
              >
                {g.ok ? '✅' : '⚠️'} {g.k.toUpperCase()} · {g.detail}
              </span>
            ))}
          </div>
        ) : null}

        {Array.isArray(gates.warnings) && gates.warnings.length ? (
          <ul className="mt-3 text-[12.5px] text-amber-800 list-disc pl-5 space-y-0.5">
            {gates.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        ) : null}
        {gates.archive_error ? (
          <div className="mt-3 text-[12.5px] text-amber-800">⚠️ ไม่ได้เก็บไฟล์ต้นฉบับ ({gates.archive_error}) — รอบนี้ย้อนกลับไปอ่านไฟล์เดิมไม่ได้</div>
        ) : null}
        {gates.parse_error ? (
          <div className="mt-3 text-[12.5px] text-rose-800">⚠️ อ่านไฟล์ไม่สำเร็จ: {gates.parse_error}</div>
        ) : null}
      </div>

      {/* stats */}
      <div className="mb-4 inline-flex flex-wrap items-baseline gap-x-5 gap-y-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5">
        {/* ⚠️ "จัดการแล้ว", NOT "ผูกแล้ว". This tile counts rows whose MONEY is settled —
            which includes 122 rows the old batch path wrote and never linked. Calling that
            ผูกแล้ว put the word on a tile reading 4/5 directly above a section titled
            "ยังไม่ได้ผูกกับไฟล์" holding 3 of those same rows. Same word, two jobs, third time
            today. ผูก now means exactly one thing on this page — a link record exists — and
            จัดการ means the money is dealt with. */}
        <span className="text-[13px] text-slate-500">จัดการแล้ว</span>
        <span className="text-[17px] font-semibold text-slate-900 tabular-nums">
          {fmt(doneStats.linked)} / {fmt(doneStats.total)} <span className="text-[13px] font-normal text-slate-500">รายการ</span>
        </span>
        {doneStats.openBaht > 0 ? (
          <span className="text-[13px] text-amber-800">
            ยอดที่ยังไม่ได้จัดการ <b className="tabular-nums">{fmt(doneStats.openBaht)} ฿</b>
          </span>
        ) : null}
        {doneStats.outsideCount ? (
          <span className="text-[12px] text-slate-500">
            ไม่นับในตัวหาร {fmt(doneStats.outsideCount)} รายการ
            {doneStats.outsideBaht > 0 ? ` · ${fmt(doneStats.outsideBaht)} ฿` : ''}
          </span>
        ) : null}
        <span className="text-[11.5px] text-slate-400">นับเป็นรายการในระบบ — 1 แถวในไฟล์อาจตรงกับหลายรายการ</span>
      </div>

      {/* ⚠️ THE WORD พร้อมบันทึก USED TO APPEAR TWICE ON THIS SCREEN MEANING TWO DIFFERENT
          THINGS, and on run #61 it read 4 here and (1) on the section heading below. This
          strip is the MATCHER's grading at IMPORT TIME and never changes; the section counts
          rows that are still waiting RIGHT NOW. Same word, two clocks — which made the
          sections look like they meant the opposite of what they say. Owner caught it,
          2026-09-15.

          Fixed by saying which clock this is. The strip is now explicitly ตอนนำเข้า and uses
          จับคู่ได้ for the matcher's verdict; พร้อมบันทึก survives in exactly one place, the
          section heading, where it means what it says: matched, and still not written. */}
      <div className="mb-5 text-[13px]">
        <div className="text-[11.5px] font-medium text-slate-400 mb-1.5">ตอนนำเข้า — ผลการจับคู่ของระบบ ณ วันที่อ่านไฟล์ (ไม่เปลี่ยนอีก)</div>
        <div className="flex flex-wrap gap-x-7 gap-y-2">
          <Stat label="แถวในไฟล์" v={fmt(run.rows_total)} />
          <Stat label="จับคู่ได้" v={fmt(run.rows_high + run.rows_medium + run.rows_low)} />
          <Stat label="กำกวม" v={fmt(run.rows_ambiguous)} />
          <Stat label="จับคู่ไม่ได้" v={fmt(run.rows_unmatched)} />
          <Stat label="รับแล้วอยู่ก่อน" v={fmt(run.rows_already_received)} />
          {/* what the OLD batch path wrote. Kept because it is this run's own history, but it
              is not the live figure — the ผูกแล้ว tile above is. */}
          {run.rows_written ? (
            <Stat label="รอบนี้เคยบันทึกเงิน" v={`${fmt(run.rows_written)} รายการ · ${fmt(run.amount_written)} ฿`} />
          ) : null}
        </div>
      </div>

      {msg ? (
        <div className={`rounded-xl border px-4 py-3 text-[13px] mb-5 ${
          msg.bad ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-emerald-200 bg-emerald-50 text-emerald-900'}`}>
          {msg.text}
        </div>
      ) : null}

      {/* action bar */}
      {selectable.size ? (
        <div className="sticky top-0 z-10 -mx-2 mb-5 rounded-xl border border-slate-200 bg-white/95 backdrop-blur px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[13.5px] text-slate-700">
              เลือก <b>{fmt(chosen.length)}</b> จาก {fmt(selectable.size)} รายการ · <b>{fmt(chosenAmount)} ฿</b>
            </span>
            {/* The one-click arm. It replaces the pre-tick rather than restoring it: same
                keystroke count for the common case, but the operator performs it. */}
            {chosen.length < selectable.size ? (
              <button type="button" onClick={() => setPicked(new Set(selectable))}
                className="text-[12.5px] text-brand-600 hover:underline">
                เลือกทั้งหมด ({fmt(selectable.size)})
              </button>
            ) : null}
            {chosen.length ? (
              <button type="button" onClick={() => setPicked(new Set())}
                className="text-[12.5px] text-slate-500 hover:text-slate-700 hover:underline">
                ล้างที่เลือก
              </button>
            ) : null}

            {/* ⚠️ AXA states no payment date in its file, so a courier-staged AXA run has
                none. It is asked for HERE rather than defaulted, because defaulting would
                stamp the day the file arrived — which is not the day the money moved, and
                is the exact defect api 570's transfer-date input was added to fix. */}
            {needsDate ? (
              <label className="flex items-center gap-2">
                <span className="text-[12.5px] font-medium text-slate-600">
                  วันที่เงินเข้าบัญชี <span className="text-rose-600">*</span>
                </span>
                <input
                  type="date" value={txDate} onChange={(e) => setTxDate(e.target.value)}
                  className="h-8 rounded-lg border border-slate-300 px-2 text-[13px]"
                />
              </label>
            ) : null}

            <div className="flex-1" />
            {!mayCommit ? (
              <span className="text-[12.5px] text-slate-500">ยืนยันได้เฉพาะ GM หรือ Admin — ตรวจรายการและขอแก้ยอดได้ตามปกติ</span>
            ) : (
              <button
                type="button"
                disabled={!chosen.length || busy || (needsDate && !txDate)}
                onClick={() => setConfirm('commit')}
                className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-brand-600 text-white text-[14px] font-medium disabled:opacity-40 hover:bg-brand-700 transition"
              >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} ตรวจสอบและยืนยัน
              </button>
            )}
          </div>
          {needsDate && !txDate ? (
            <p className="mt-2 text-[12px] text-amber-800">
              ไฟล์ของ {run.payer} ไม่มีวันที่โอนเงินอยู่ในไฟล์ — ต้องกรอกวันที่จาก statement ของธนาคารก่อนจึงจะบันทึกได้
              (ถ้าปล่อยว่าง ระบบจะบันทึกวันที่กดปุ่มแทนวันที่เงินเข้าจริง จึงไม่อนุญาต)
            </p>
          ) : null}
        </div>
      ) : null}

      {isCommitted && mayCommit && revertable.length ? (
        <div className="mb-5">
          <button
            type="button"
            disabled={busy}
            onClick={() => setConfirm('revert')}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-slate-300 bg-white text-slate-700 text-[13.5px] font-medium hover:bg-slate-50 transition"
          >
            <RotateCcw size={15} /> ย้อนกลับรอบนี้ ({fmt(revertable.length)} รายการ)
          </button>
        </div>
      ) : null}

      {/* sections */}
      {SECTIONS.map((s) => {
        const list = grouped.get(s.key) || []
        if (!list.length) return null
        const isOpen = open.has(s.key)
        const canTick = s.writable && mayCommit
        return (
          <div key={s.key} className={`rounded-2xl border mb-4 overflow-hidden ${TONE[s.tone]}`}>
            <button
              type="button"
              onClick={() => setOpen((p) => { const n = new Set(p); n.has(s.key) ? n.delete(s.key) : n.add(s.key); return n })}
              className="w-full flex items-center gap-2 px-4 py-3 text-left"
            >
              {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              <s.Icon size={15} />
              <span className="text-[14px] font-semibold text-slate-800">{s.title}</span>
              <span className="text-[13px] text-slate-500">({fmt(list.length)})</span>
              {(() => {
                const n = list.filter((r) => reqByRow.get(r.id)?.status === 'pending').length
                return n ? <span className="rounded px-1.5 py-0.5 text-[11px] bg-amber-200 text-amber-900">รออนุมัติ {fmt(n)}</span> : null
              })()}
              {canTick ? (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation()
                    const ids = list.map((r) => r.id).filter((x) => selectable.has(x))
                    const allOn = ids.every((x) => picked.has(x))
                    setPicked((p) => { const n = new Set(p); ids.forEach((x) => (allOn ? n.delete(x) : n.add(x))); return n })
                  }}
                  className="ml-auto text-[12.5px] text-brand-700 hover:underline"
                >
                  เลือก/ยกเลิกทั้งกลุ่ม
                </span>
              ) : null}
            </button>

            {s.note && isOpen ? <p className="px-4 pb-2 -mt-1 text-[12.5px] text-slate-600">{s.note}</p> : null}

            {isOpen ? (
              <div className="bg-white/80 overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead className="text-slate-500">
                    <tr className="border-t border-slate-200">
                      <th className="w-8" />
                      <th className="px-2 py-2 text-left">#</th>
                      <th className="px-2 py-2 text-left">ชื่อในไฟล์</th>
                      <th className="px-2 py-2 text-left">อ้างอิง</th>
                      <th className="px-2 py-2 text-left">วันที่รักษา</th>
                      <th className="px-2 py-2 text-right">จ่าย</th>
                      <th className="px-2 py-2 text-left">HN · ชื่อในระบบ</th>
                      <th className="px-2 py-2 text-left">วิธีจับคู่</th>
                      <th className="px-2 py-2 text-left">มั่นใจ</th>
                      <th className="px-2 py-2 text-left">หมายเหตุ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((r) => {
                      const showExp = expanded.has(r.id)
                    /* ⚠️ PER ROW, not per run. The before/after panel used to ask
                       `isStaged`, which is a statement about the FILE, not about this line —
                       so on a committed run holding open rows it announced in the past tense
                       ("เปลี่ยนอะไรไปบ้าง") what had not happened yet, and printed the stale
                       before_* snapshot as though it were the result. Those runs are exactly
                       where the ฿271,525 was sitting. */
                    const willChange = !isRowDone(r)
                    /* ⚠️ THE after_* COLUMNS BELONG TO THE OLD MODEL AND THE BULK LINK DOES NOT
                       WRITE THEM. api 656 records before/after into claim_import_link_event,
                       where the history now lives, and leaves claim_import_row.after_* alone.
                       Rendering those empty fields as the RESULT told the operator, on a row
                       that had just linked correctly, that advance_status became "ว่าง" and
                       advance_received_by became 0 — money looking destroyed when nothing had
                       happened to it. Found by the owner on the first real batch, 2026-09-15.
                       A row carrying no snapshot now shows what the LIVE line says, plus what
                       the link actually did, and never a blank. */
                    const wroteSnapshot = !!String(r.after_advance_status || '')
                    /* ⚠️ reason_detail IS FROZEN AT IMPORT and its amount sentence goes stale
                       the moment anyone corrects the claim. Measured on run 87, 2026-09-15:
                       three rows read "ไฟล์ 824.51 · ระบบ 785" while billing had ALREADY been
                       corrected to ฿824 — so the note said the money disagreed, the button
                       correctly said it agreed, and the owner reasonably believed the button
                       was the broken one. Sixth time today this screen showed the import's
                       memory instead of the live money. The live pair is computed here from
                       the billing join the page already holds; the import-time figure is kept
                       beside it and LABELLED, because "it used to say 785" is still the useful
                       half of the story when someone is asking why a row moved. */
                    const lbNote = live.get(Number(r.billing_id))
                    const liveOut = lbNote
                      ? (Number(lbNote.priceadvance) || 0) - (Number(lbNote.advance_received_amount) || 0)
                      : null
                    const filePaid = Number(r.file_amount_paid)
                    /* ⚠️ ONLY where the frozen note was ITSELF an amount sentence. reason_detail
                       also carries จับคู่ได้หลายรายการ, ชื่อกำกวม and the rest — replacing those
                       with an amount pair would delete the only explanation the row has. */
                    const wasAmountNote = /ระบบ/.test(String(r.reason_detail || ''))
                    const showLivePair = wasAmountNote && liveOut != null && Number.isFinite(filePaid) && filePaid > 0
                    const staleNote = showLivePair
                      && Math.abs(Math.round(filePaid * 100) - Math.round(liveOut * 100)) <= (payerTolerance(run.payer) || 0)
                    /* ⚠️ ตรวจจับคู่ใหม่ APPEARS ONLY WHERE THE TWO FIGURES ACTUALLY DISAGREE, per
                       the owner. That is the whole case it serves: a figure only stops agreeing
                       because somebody corrected the bill or opened a second one AFTER the file
                       was read, and the matcher has never looked since. On a row whose money
                       already agrees there is nothing to re-ask, and the button would just be
                       another thing to press on a money screen.
                       ⚠️ Judged on LIVE billing, not on `outcome` — the point is to notice a
                       change the frozen verdict cannot see. It disappears by itself once the
                       figures agree, which is also how the operator knows it worked. */
                    const liveGap = (lbNote && Number.isFinite(filePaid) && filePaid > 0)
                      ? Math.round(filePaid * 100) - Math.round(liveOut * 100)
                      : null
                    const mayRematch = mayCommit && liveGap != null
                      && Math.abs(liveGap) > (payerTolerance(run.payer) || 0)
                      && !['linked', 'dismissed'].includes(String(r.link_state || ''))
                      && r.write_confirmed !== true
                      const c = CONF[r.confidence] || CONF.none
                      return (
                        <>
                          <tr key={r.id} className="border-t border-slate-100 align-top">
                            <td className="px-2 py-1.5">
                              {canTick && selectable.has(r.id) ? (
                                <input
                                  type="checkbox"
                                  checked={picked.has(r.id)}
                                  onChange={() => setPicked((p) => {
                                    const n = new Set(p)
                                    const ids = (splitSets.get(r.id) || [r.id]).filter((x) => selectable.has(x))
                                    const on = !n.has(r.id)
                                    ids.forEach((x) => (on ? n.add(x) : n.delete(x)))
                                    return n
                                  })}
                                />
                              ) : null}
                            </td>
                            <td className="px-2 py-1.5 text-slate-400">
                              {r.seq}
                              {splitSets.has(r.id) && (
                                <span className="ml-1 rounded px-1 py-0.5 text-[10.5px] bg-brand-50 text-brand-700 ring-1 ring-brand-100"
                                  title="แถวนี้ในไฟล์จ่ายหลายรายการรวมกัน — เลือกและยืนยันพร้อมกันทั้งชุด">
                                  ชุด {splitSets.get(r.id).length}
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-1.5">{r.file_patient_name || '—'}</td>
                            <td className="px-2 py-1.5"><code className="text-[11.5px]">{r.claim_ref || '—'}</code></td>
                            <td className="px-2 py-1.5">{d10(r.file_service_date)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.file_amount_paid)}</td>
                            <td className="px-2 py-1.5">
                              {r.billing_id ? (
                                <>
                                  {/* patient_id is the NUMBER 0 on an unmatched row — PatientLink's own
                                      `> 0` guard handles it and renders the text unwrapped. The
                                      (ต่างสาขา) badge stays OUTSIDE the anchor. No onOpen: this <tr>
                                      has no onClick to protect. */}
                                  <PatientLink patientId={r.patient_id} icon
                                    className="group inline-block leading-tight">
                                    <span className="block group-hover:font-semibold group-hover:text-brand-700 transition-colors">
                                      {r.crm_name}
                                    </span>
                                    <span className="block text-[11.5px] text-slate-500 tabular-nums group-hover:font-semibold group-hover:text-brand-700 transition-colors">
                                      {r.hn}
                                    </span>
                                  </PatientLink>
                                  {r.cross_branch ? <span className="ml-1 text-[11px] text-amber-700">(ต่างสาขา)</span> : null}
                                </>
                              ) : <span className="text-slate-400">—</span>}
                            </td>
                            <td className="px-2 py-1.5 text-slate-500">{r.match_tier || '—'}</td>
                            <td className="px-2 py-1.5">
                              <span className={`rounded px-1.5 py-0.5 text-[11px] ${c.cls}`}>{c.label}</span>
                            </td>
                            <td className="px-2 py-1.5 text-slate-600">
                              {showLivePair ? (
                                <>
                                  <span className={staleNote ? 'text-emerald-700' : ''}>
                                    ไฟล์ <span className="tabular-nums">{fmt(filePaid)}</span>
                                    {' · '}ระบบ <span className="tabular-nums">{fmt(liveOut)}</span>
                                    {staleNote ? ' — ยอดตรงกันแล้ว' : ''}
                                  </span>
                                  {staleNote ? (
                                    <span className="block text-[11px] text-slate-400">
                                      ตอนนำเข้าระบบเคยเป็น {String(r.reason_detail || '').replace(/^.*ระบบ\s*/, '')} · มีการแก้ยอดเบิกหลังจากนั้น
                                    </span>
                                  ) : null}
                                </>
                              ) : (r.reason_detail || r.reason_code || '')}
                              {r.billing_id ? (
                                <button
                                  type="button"
                                  onClick={() => setExpanded((p) => { const n = new Set(p); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n })}
                                  className="ml-2 text-brand-700 hover:underline"
                                >
                                  {showExp ? 'ซ่อน' : (willChange ? 'จะเปลี่ยนอะไรบ้าง' : 'เปลี่ยนอะไรไปบ้าง')}
                                </button>
                              ) : null}
                              {/* ขอแก้ยอดเบิก — only where the money is what parked the row.
                                  Never on to_write (nothing to fix) and never on unmatched
                                  (no billing line to aim at). */}
                              {/* ⚠️ Every section except the two that are nothing to do
                                  with linking. A `not_payable` row has nothing to link to,
                                  and a crm_leftover is not a file row at all. */}
                              {s.key !== 'not_payable' && r.kind !== 'crm_leftover' ? (
                                <div className="mt-1">
                                  <LinkControl runId={run.id} row={r} mayLink={mayCommit}
                                    bulkHandles={selectable.has(r.id)}
                                    liveStatus={live.get(Number(r.billing_id))?.advance_status}
                                    liveAmount={live.get(Number(r.billing_id))?.priceadvance}
                                    liveReceived={live.get(Number(r.billing_id))?.advance_received_amount}
                                    toleranceSatang={payerTolerance(run.payer)}
                                    onDone={load} />
                                </div>
                              ) : null}
                              {/* ⚠️ IN THE NOTE CELL, NOT IN THE EXPANDER. The first attempt
                                  anchored this next to the ผูกแบบ line, which lives inside the
                                  จะเปลี่ยนอะไรบ้าง panel — so the button existed but only appeared
                                  after the operator expanded the row, which is the one place
                                  they would not think to look for it. Row 5 of run 87 rendered
                                  without it and the owner reported it missing a second time. */}
                              {mayRematch ? (
                                <div className="mt-1">
                                  <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => rematch(r.id)}
                                    title="ยอดในไฟล์กับยอดเบิกในระบบไม่ตรงกัน — ให้ระบบจับคู่ใหม่กับบิลที่มีอยู่ตอนนี้ (ไม่แตะเงิน ไม่แตะแถวที่ผูกแล้ว)"
                                    className="inline-flex items-center gap-1 h-7 px-2 rounded border border-slate-300 bg-white text-[11.5px] text-slate-600 hover:border-brand-300 hover:text-brand-700 disabled:opacity-40"
                                  >
                                    {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} ตรวจจับคู่ใหม่
                                  </button>
                                </div>
                              ) : null}
                              {(s.key === 'amount_mismatch' || s.key === 'amount_partial') ? (
                                <div className="mt-1">
                                  <AmountRequestCell
                                    row={r}
                                    payer={run.payer}
                                    req={reqByRow.get(r.id)}
                                    mayRequest={willChange && mayAskAmount}
                                    mayApprove={mayOkAmount}
                                    onOpen={() => setAskRow(r)}
                                    onDecided={load}
                                  />
                                </div>
                              ) : null}
                            </td>
                          </tr>
                          {showExp ? (
                            <tr key={`${r.id}-x`} className="bg-slate-50/80 border-t border-slate-100">
                              <td />
                              <td colSpan={9} className="px-2 py-2">
                                <div className="text-[11.5px] text-slate-600 mb-1">billing #{r.billing_id} · treatment {r.treatment_id}</div>
                                <table className="text-[11.5px]">
                                  <thead className="text-slate-400">
                                    <tr>
                                      <th className="pr-6 text-left">ฟิลด์</th>
                                      <th className="pr-6 text-left">ตอนนี้</th>
                                      <th className="text-left">{willChange ? 'หลังกดยืนยัน' : (wroteSnapshot ? 'หลังบันทึก' : 'ผลตอนนี้')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {/* ⚠️ While a run is STAGED the after_* columns are empty because nothing has
                                        been written yet — rendering that as "ว่าง" made it read as a PREDICTION
                                        that the field would be emptied. On claimRef that is both alarming and
                                        false: this importer never writes claimRef (see _stage.js / commit.js).
                                        So before a commit we show what WILL happen; after one, what did. */}
                                    {(() => {
                                      const lb = live.get(Number(r.billing_id))
                                      return (
                                        <>
                                          <BA k="advance_status" a={lb ? (lb.advance_status || 'ว่าง') : r.before_advance_status}
                                            b={willChange ? 'received' : (wroteSnapshot ? r.after_advance_status : (lb ? (lb.advance_status || 'ว่าง') : '—'))} predicted={willChange} />
                                          <BA k="advance_received_at" a={lb ? d10(lb.advance_received_at) : d10(r.before_advance_received_at)}
                                      b={willChange ? d10toISO(r.file_transfer_date || run.transfer_date) : (wroteSnapshot ? d10(r.after_advance_received_at) : (lb ? d10(lb.advance_received_at) : '—'))} predicted={willChange} />
                                          <BA k="advance_received_by" a={lb ? lb.advance_received_by : r.before_advance_received_by}
                                            b={willChange ? (me?.name || 'คุณ') : (wroteSnapshot ? r.after_advance_received_by : (lb ? lb.advance_received_by : '—'))} predicted={willChange} />
                                          <BA k="claimRef" a={lb ? (lb.claim_ref || '') : r.before_claim_ref}
                                            b={willChange ? r.before_claim_ref : (r.after_claim_ref || r.before_claim_ref)}
                                            predicted={willChange} unchanged />
                                        </>
                                      )
                                    })()}
                                  </tbody>
                                </table>
                                {!willChange && !wroteSnapshot && r.link_kind ? (
                                  <div className="mt-1.5 text-[11.5px] text-slate-500">
                                    ผูกแบบ <b className="text-slate-700">{LINK_KIND_TH[r.link_kind] || r.link_kind}</b>
                                    {r.linked_at ? ` · ${d10(r.linked_at)}` : ''}
                                    {r.link_kind === 'already_received'
                                      ? ' — บิลนี้มีคนบันทึกรับเงินไว้ก่อนแล้ว ระบบจึงไม่แก้ยอดหรือวันที่เดิม'
                                      : ''}
                                  </div>
                                ) : null}
                                {r.restored_exactly === false && r.reverted_by_run_id ? (
                                  <div className="mt-1 text-[11.5px] text-amber-700">
                                    ⚠️ ค่าเดิมเป็นค่าว่าง แต่ย้อนกลับได้แค่ “pending” (ผลลัพธ์เหมือนกัน แต่ไม่ตรงตัวอักษร)
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          ) : null}
                        </>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        )
      })}

      {askRow ? (
        <AmountRequestDialog row={{ ...askRow, payer: run.payer }} onClose={() => setAskRow(null)} onDone={load} />
      ) : null}

      {confirm === 'commit' ? (
        <ConfirmDialog
          title="ตรวจสอบและยืนยัน?"
          message={
            `ผูก ${fmt(chosen.length)} รายการ รวม ${fmt(chosenAmount)} ฿` +
            ((run.transfer_date || txDate) ? ` · วันที่เงินเข้า ${d10(run.transfer_date || txDate)}` : '') +
            ' · ระบบจะอ่านสถานะล่าสุดของทุกบิลก่อนเขียน — บิลที่มีคนบันทึกรับเงินไปแล้ว'
            + 'จะผูกเฉย ๆ โดยไม่แก้ยอดหรือวันที่เดิม และรายการที่ยอดไม่ตรงจะถูกข้ามพร้อมบอกเหตุผล'
          }
          confirmLabel="บันทึก"
          onConfirm={() => submit(false)}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

      {confirm === 'revert' ? (
        <ConfirmDialog
          title="ย้อนกลับรอบนี้?"
          message={
            `ย้อนกลับ ${fmt(revertable.length)} รายการ · ` +
            'รายการที่เดิมเป็นค้างรับจะกลับเป็นค้างรับ · รายการที่เดิมรับแล้วอยู่ก่อน (แก้เฉพาะวันที่) จะคืนเป็นวันที่เดิม ไม่ถูกเปลี่ยนเป็นค้างรับ · ' +
            'ชื่อผู้บันทึกจะกลายเป็นชื่อคุณ เพราะระบบเขียนคืนชื่อเดิมไม่ได้'
          }
          confirmLabel="ย้อนกลับ"
          tone="danger"
          onConfirm={() => submit(true)}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

      {/* ⚠️ A SEPARATE state, not a third value on `confirm`. Widening that string to an
          object would touch both existing money dialogs. House rule: never window.confirm. */}

    </div>
  )
}

function Row({ k, v }) {
  return <div className="flex gap-2"><dt className="text-slate-500 shrink-0">{k}</dt><dd className="text-slate-800 min-w-0">{v}</dd></div>
}
function Stat({ label, v, strong }) {
  return (
    <div>
      <div className="text-[11.5px] text-slate-500">{label}</div>
      <div className={`tabular-nums ${strong ? 'text-[15px] font-semibold text-slate-900' : 'text-[14px] text-slate-700'}`}>{v}</div>
    </div>
  )
}
/**
 * One field's before → after.
 *
 * `unchanged` marks a field this importer never writes — claimRef. It is called out
 * explicitly rather than left to look like a coincidence, because the reference number
 * is the AXA join key and "will my reference survive this?" is the first thing anyone
 * sensible asks when they see a before/after table next to their money.
 */
function BA({ k, a, b, predicted, unchanged }) {
  const A = String(a ?? '')
  const B = String(b ?? '')
  const changed = A !== B
  return (
    <tr>
      <td className="pr-6 text-slate-500">{k}</td>
      <td className="pr-6 text-slate-700">{A || <span className="text-slate-300">ว่าง</span>}</td>
      <td className={changed ? (predicted ? 'text-brand-700 font-medium' : 'text-emerald-700 font-medium') : 'text-slate-400'}>
        {unchanged && !changed
          ? <span className="text-slate-400">ไม่เปลี่ยน</span>
          : (B || <span className="text-slate-300">ว่าง</span>)}
        {predicted && changed ? <span className="text-slate-400 font-normal"> (ยังไม่บันทึก)</span> : null}
      </td>
    </tr>
  )
}

// A stored ISO date passes through; anything else renders as an em dash.
const d10toISO = (v) => (v ? String(v).slice(0, 10) : '—')
